/**
 * **D166 — the iOS transport, and the only thing in `web/` that knows a native container exists
 * besides `pwa/nativeContainer.ts`.**
 *
 * **Why it exists.** An API Gateway HTTP API **refuses a non-`http(s)` scheme in `allowOrigins`**
 * (`BadRequestException: Invalid format for origin`, measured on both `capacitor://` and
 * `selogate://`), so the iOS WebView's `capacitor://<host>` origin cannot be admitted by CORS on any
 * stage — not as a configuration choice, but because the platform will not hold the value. From the
 * app's own WebView against staging the refusal is witnessed rather than assumed: the preflight
 * answered `204`, `access-control-allow-origin` was absent, and app code saw `Load failed`. Android
 * is untouched and needs none of this — `androidScheme: 'https'` already makes its origin
 * byte-identical to the web origin (D161(c)), so it keeps the WebView's own stack.
 *
 * **What is scoped, and why the scoping is the decision rather than a detail.** Capacitor ships a
 * config flag (`plugins.CapacitorHttp.enabled`) that patches `window.fetch` and `XMLHttpRequest`
 * app-wide. It stays **off**. Turning it on would silently reroute **P17's** Places/Geocoding client
 * — a surface that owns its own `fetchImpl` seam and has nothing to do with this problem — through
 * the native stack, where its errors, its timeouts and its referrer restriction all mean something
 * different. What is rerouted here is the `ApiTransport` seam and nothing else: the global `fetch`
 * is left exactly as the platform supplied it.
 *
 * **Reached through the injected global, never an import.** `web/` must not depend on
 * `@capacitor/core` — the shell is P6's and the container is P18's, and `native/` consumes the built
 * bundle rather than the other way round. `pwa/nativeContainer.ts` says the same thing about the
 * same global for the same reason.
 *
 * **The member read is `Capacitor.nativePromise`, and `Capacitor.Plugins.CapacitorHttp` would not
 * work.** The native bridge injects `getPlatform`, `isNativePlatform`, `toNative` and
 * `nativePromise` itself, before app code runs. `Capacitor.Plugins.<name>` is different: those
 * proxies are created by `@capacitor/core`'s `registerPlugin` at *import* time, so in a bundle that
 * refuses that import the plugin namespace is empty and the obvious-looking call is `undefined`.
 * `nativePromise('CapacitorHttp', 'request', …)` is the same door Capacitor's own patched `fetch`
 * uses. **The plugin itself is registered unconditionally** — `CAPHttpPlugin` is in the iOS
 * bridge's core plugin list, and the `enabled` flag is read only when deciding whether to patch
 * `fetch`/`XHR` — which is what makes calling it with the flag off legitimate rather than a trick.
 *
 * **§4.7's one authenticated door is untouched.** This module attaches no header, reads no
 * credential, and inspects no status code; it moves bytes, exactly as `fetchTransport` does and for
 * the same stated reason. The bearer and the binding are still attached in one place, in
 * `session.ts`, and the only thing that changed is which network stack that one function's request
 * is handed to.
 *
 * **The acceptance criterion is the error taxonomy below, not the compile.** The client's rules are
 * that a `NetworkError` means *degrade to offline-read-only* (seven call sites catch it for that
 * meaning), that `SessionUnavailableError extends NetworkError` so those seven stay byte-identical,
 * and that **only a `401` ends a session** — which costs a real SMS to undo (D38). A transport that
 * reports a `401` as a network failure never ends a session and strands the user behind a screen
 * that will not sign in; one that reports a blip as `401` buys SMSs. `Load failed` above is exactly
 * how indistinguishable those are on this platform. So the mapping is stated once, here, and
 * pinned by `nativeHttp.test.ts` — and only a device pass over airplane mode, a `5xx` and an expired
 * token closes it, because nothing in jsdom and nothing driven against `dev/devTransport.ts` ever
 * reaches this code.
 */

import { API_BASE_URL } from './config'
import { REQUEST_TIMEOUT_MS, TIMED_OUT, deadline } from './timeout'
import {
  NetworkError,
  RequestTimeoutError,
  field,
  numberField,
  type ApiResponse,
  type ApiTransport,
} from './transport'

/**
 * The three members the native bridge injects that this module reads. Duck-typed rather than
 * imported, and every one of them optional, because the same bundle runs in a browser where the
 * global is absent entirely.
 */
interface CapacitorBridge {
  getPlatform?: () => string
  isNativePlatform?: () => boolean
  nativePromise?: (pluginName: string, methodName: string, options: unknown) => Promise<unknown>
}

function bridge(): CapacitorBridge | undefined {
  return (globalThis as typeof globalThis & { Capacitor?: CapacitorBridge }).Capacitor
}

/**
 * Whether this launch must use the native stack — **iOS in a native container, and nothing else.**
 *
 * Android is deliberately excluded rather than merely not needed: its origin equals the web origin,
 * so its WebView's `fetch` traverses the allow-list exactly as a browser's does, and routing it
 * through a second HTTP stack would trade a working path for an untested one. A browser answers
 * `false` on the first term, since the global is not there at all.
 *
 * Read per call rather than latched at module load. The bridge is injected before app code runs, so
 * a latch would be correct too — but a per-call read costs three property lookups, cannot go stale,
 * and is what lets a test set and unset the global between cases without reloading the module.
 */
export function usesNativeHttp(): boolean {
  const capacitor = bridge()
  return (
    typeof capacitor?.nativePromise === 'function' &&
    typeof capacitor.isNativePlatform === 'function' &&
    capacitor.isNativePlatform() === true &&
    typeof capacitor.getPlatform === 'function' &&
    capacitor.getPlatform() === 'ios'
  )
}

/**
 * Read the plugin's answer into the shape `session.ts` already expects.
 *
 * **A status is carried, never invented.** The plugin resolves for *every* HTTP status — a `401`, a
 * `500` and a `200` all arrive here as resolutions with a `status` field, and only a transport-level
 * failure rejects. That split is the whole taxonomy: a status that arrives is a status the server
 * sent, so it is passed through untouched and `request()` decides what it means.
 *
 * **An answer with no usable status is a network failure, not a status.** Defaulting it would be
 * inventing a server response out of a broken bridge — and every default is wrong: `401` signs the
 * user out, `200` hands an empty body to a reader expecting fields, `500` claims the server
 * answered when nothing did.
 *
 * **The body is read the way `fetchTransport` reads it**, because the two must be
 * indistinguishable to every caller. The native side parses JSON itself when the response carries a
 * JSON content-type and hands back a string otherwise; a string is therefore re-parsed here and
 * falls back to itself, which is `fetchTransport`'s own rule for a non-JSON body. `204`/`205` are
 * pinned to `undefined` ahead of that: with a JSON content-type and an empty body the native parse
 * fails and returns *its own error description as the data*, which would arrive as a plausible
 * string body for a response that by definition has none.
 */
export function nativeResponseOf(raw: unknown): ApiResponse {
  const status = numberField(raw, 'status')
  if (status === undefined) throw new NetworkError(new Error('native-http-response-unusable'))

  const data = field(raw, 'data')
  if (status === 204 || status === 205) return { status, body: undefined }
  if (typeof data !== 'string') return { status, body: data }
  if (data.length === 0) return { status, body: undefined }
  try {
    return { status, body: JSON.parse(data) as unknown }
  } catch {
    return { status, body: data }
  }
}

/**
 * The native stack's `ApiTransport`. Same contract as `fetchTransport`: one request in, a status
 * and a body out, a `NetworkError` for anything that never became a response.
 *
 * **The body is serialized here, not natively.** The plugin accepts an object and would run its own
 * JSON serializer over it; handing it the string `fetchTransport` would have sent keeps one
 * serializer for both platforms, so a Hebrew display name or a number cannot cross the wire in two
 * shapes depending on which build the user installed. The plugin only attaches a body at all when a
 * `Content-Type` header is present, which is why the header and the data are set by the same
 * condition below rather than independently.
 *
 * **Two deadlines, and both are needed.** `REQUEST_TIMEOUT_MS` is handed to the native side so the
 * request itself gives up (its own default is ten minutes), and R1-1's `deadline()` wraps the call
 * so the *caller* is freed even if the bridge never answers — a bridge message that is dropped
 * settles nothing, and that is the one-`await`-with-no-timeout shape R1-1 exists to remove. There is
 * no abort to pair with it the way `fetchTransport` pairs one with `fetch`: a native request in
 * flight belongs to the platform, so what the deadline buys here is an answer, not a cancellation.
 */
export const nativeHttpTransport: ApiTransport = async (request) => {
  const capacitor = bridge()
  const call = capacitor?.nativePromise
  // Unreachable behind `usesNativeHttp()`, and a `NetworkError` rather than a throw because the
  // honest reading of a bridge that vanished mid-session is *unreachable*, which is a state this
  // app already renders.
  if (typeof call !== 'function') throw new NetworkError(new Error('capacitor-bridge-absent'))

  const hasBody = request.body !== undefined
  const budget = deadline(REQUEST_TIMEOUT_MS)
  try {
    const raw = await budget.settle(
      call.call(capacitor, 'CapacitorHttp', 'request', {
        url: `${API_BASE_URL}${request.path}`,
        method: request.method,
        headers: {
          ...(hasBody ? { 'content-type': 'application/json' } : {}),
          ...request.headers,
        },
        ...(hasBody ? { data: JSON.stringify(request.body) } : {}),
        connectTimeout: REQUEST_TIMEOUT_MS,
        readTimeout: REQUEST_TIMEOUT_MS,
      }),
    )
    if (raw === TIMED_OUT) throw new RequestTimeoutError(new Error('request-timeout'))
    return nativeResponseOf(raw)
  } catch (error) {
    // Every rejection is a reachability failure: the plugin rejects on a `URLSession` error —
    // offline, DNS, TLS, its own timeout — and on nothing else. Re-wrapping our own error would
    // bury the cause for no gain, and inventing a status here is the defect this whole module is
    // written to avoid.
    throw error instanceof NetworkError ? error : new NetworkError(error)
  } finally {
    budget.cancel()
  }
}

/**
 * Wrap the web transport so iOS takes the native stack and every other platform takes the one it
 * was given. **One line at the composition root, and the only wiring this decision needs.**
 *
 * It decorates rather than replaces because the fallback is not always `fetchTransport`: a
 * development build with no API configured is handed `dev/devTransport`, and that must keep
 * answering from the fake far end rather than being sent at a network that is not there.
 *
 * **Every `ApiTransport` call is covered, and that is deliberate.** D166 names
 * `SessionManager.request()`, which is where the authenticated calls go — but `POST /auth/refresh`
 * is issued from `#doRefreshLocked` and the two OTP calls from `AuthProvider`'s `publicCall`, and
 * CORS refuses all three from a `capacitor://` origin identically. Routing only the first would
 * leave an iOS build that cannot sign in and cannot refresh, so the decision's own acceptance
 * criterion — a device pass over an expired token — could not be run at all. Logged as a deviation
 * in `implementation-notes.md` → P18.
 */
export function nativeApiTransport(fallback: ApiTransport): ApiTransport {
  return (request) => (usesNativeHttp() ? nativeHttpTransport(request) : fallback(request))
}
